export default class AudioIntensityAnalyzer {
  /**
   * The first frequency bin to start picking from
   */
  public static frequencyOffset = 4

  /**
   * the number of frequency bins to use in the analysis
   */
  public static frequencyBinNumber = 2

  // The current value of audioIntensity used for Filters
  private static weightedIntensity = 0
  // The current rate of change of the weightedIntensity
  private static intensityVelocity = 0

  private static getWeightedIntensity (audioIntensity: number) {
    const attackFactor = 1
    const releaseFactor = 3
    const diff = audioIntensity - this.weightedIntensity
    // Calculate the velocity of change of the audioIntensity incorporating the attack and release factors
    const newIntensityVelocity = diff > 0 ? diff / attackFactor : diff / releaseFactor
    // Average new velocity with existing velocity to add smoothing
    this.intensityVelocity = (this.intensityVelocity * 2 + newIntensityVelocity) / 3
    this.weightedIntensity += this.intensityVelocity
    this.weightedIntensity = Math.max(0, this.weightedIntensity)
    return this.weightedIntensity
  }

  public static resetWeightedIntensity () {
    this.weightedIntensity = 0
  }

  /**
   * Frequency bins are linearly spread from 0 to 1/2 of the sample rate. The audio sample rate is 44100Hz, so each bin
   * has a range of 21.5Hz. The frequencies that we want to analyse are in bins 4 & 5 (86 to 129Hz)
   *
   * @param {number[]} audioFrequencyData - each element of the array represents a frequency intensity
   * @param {number} meanIntensity - we ignore values below min intensity
   *
   * @returns {number} the average value of the selected frequencies
   */
  public static getAudioIntensity (
    audioFrequencyData : number[],
    meanIntensity = 0
  ): number {
    if (audioFrequencyData == null || audioFrequencyData.length === 0) {
      return 0
    }

    let intensitySum = 0.0
    for (let i = 0; i < AudioIntensityAnalyzer.frequencyBinNumber; i++) {
      const originalValue = audioFrequencyData[AudioIntensityAnalyzer.frequencyOffset + i]
      // calculate peak distance from meanIntensity
      const peak = Math.max(originalValue - meanIntensity, 0)
      intensitySum += peak
    }

    const avgIntensity = intensitySum / AudioIntensityAnalyzer.frequencyBinNumber
    // Normalize taking the maxPossible intensity minus the mean
    const normalisedIntensity = avgIntensity / (255 - meanIntensity)
    return AudioIntensityAnalyzer.getWeightedIntensity(normalisedIntensity)
  }

  /**
   * @param {Uint8Array[]} frequencyData - The frequency data is composed of integers on a scale from 0 to 255.
   * Each item in the array represents the decibel value for a specific frequency.
   */
  public getBackgroundNoiseIntensity (frequencyData: Uint8Array[]): number {
    if (frequencyData.length === 0) {
      return 0
    }

    const commonSoundIntensities = frequencyData.map(intensity => this.getMostCommonIntensity(intensity))
    const intensitiesSum = commonSoundIntensities.reduce((sum, intensity) => sum + intensity, 0)

    return Math.ceil(intensitiesSum / commonSoundIntensities.length)
  }

  /**
   * @param {Uint8Array} frequencyIntensities - The frequency data is composed of integers on a scale from 0 to 255.
   * Each item in the array represents the decibel value for a specific frequency.
   */
  private getMostCommonIntensity (frequencyIntensities: Uint8Array): number {
    const audioData = AudioIntensityAnalyzer.getSelectedFrequencyRangeData(frequencyIntensities)
    const sortedIntensities = this.getSortedIntensities(audioData)

    // get x most common intensities
    const selectedIntensities: number[] = []
    const intensityNo = Math.min(sortedIntensities.length, 3) // The number or the most used intensities to pick.
    for (let i = 0; i < intensityNo; i++) {
      selectedIntensities.push(sortedIntensities[i])
    }

    const intensitySum = selectedIntensities.reduce((sum, intensity) => sum + intensity, 0)

    // avg of selected intensities
    return Math.ceil(intensitySum / selectedIntensities.length)
  }

  /**
   * @param {number[]} frequencyIntensities - frequency intensities
   */
  private getSortedIntensities (frequencyIntensities: number[]) : number[] {
    // count how repetitive the intensities are
    const intensityCounts = new Map<number, number>()
    frequencyIntensities.forEach((intensity) => {
      const currentVal = intensityCounts.get(intensity)
      const newVal = currentVal ? currentVal + 1 : 1
      intensityCounts.set(intensity, newVal)
    })

    // return sorted intensities (DESC)
    return [...intensityCounts.keys()].sort( (a, b) => {
      const intensityACount = intensityCounts.get(a)
      const intensityBCount = intensityCounts.get(b)

      if (!intensityACount || !intensityBCount) {
        return 0
      }

      // order by frequency (ASC) if the values are equal
      if (intensityACount === intensityBCount) {
        return a - b
      }

      return intensityBCount - intensityACount
    })
  }

  /**
   * Get intensities of selected frequencies. By default we pick the beat range (86 to 193.5Hz).
   *
   * @param {Uint8Array} frequencyIntensities - The frequency data is composed of integers on a scale from 0 to 255.
   * Each item in the array represents the decibel value for a specific frequency.
   */
  private static getSelectedFrequencyRangeData (frequencyIntensities: Uint8Array) : number[] {
    const audioData : number[] = []
    for (let i = 0; i < AudioIntensityAnalyzer.frequencyBinNumber; i++) {
      audioData.push(frequencyIntensities[AudioIntensityAnalyzer.frequencyOffset + i])
    }

    return audioData
  }
}
